import type { CSSProperties, FC, ReactNode } from 'react'

import { ArrowRight, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { agentCount, teams } from '@/constants/agents'
import { siteConfig } from '@/constants/site'

const stats = [
    { value: String(agentCount), label: 'AI agents' },
    { value: String(teams.length), label: 'specialist teams' },
    { value: '1', label: 'conversation' },
    { value: '24/7', label: 'always working' }
]

const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' className='relative isolate overflow-hidden pb-20 pt-36 sm:pb-28 sm:pt-44'>
            <div aria-hidden='true' className='bg-stars absolute inset-0 -z-10 opacity-80' />

            <div aria-hidden='true' className='absolute left-1/2 top-[46%] -z-10 -translate-x-1/2 -translate-y-1/2'>
                <div className='orbit-spin relative size-[760px] rounded-full border border-dashed border-white/[0.07] sm:size-[980px]' style={{ '--orbit-duration': '240s' } as CSSProperties}>
                    <span className='absolute left-1/2 top-0 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#e879f9] shadow-[0_0_16px_#e879f9]' />
                    <span className='absolute bottom-0 left-1/2 size-2 -translate-x-1/2 translate-y-1/2 rounded-full bg-[#fbbf24] shadow-[0_0_14px_#fbbf24]' />
                </div>
                <div className='orbit-spin absolute inset-[18%] rounded-full border border-white/[0.06]' style={{ '--orbit-duration': '180s', '--orbit-direction': 'reverse' } as CSSProperties}>
                    <span className='absolute right-0 top-1/2 size-2 -translate-y-1/2 translate-x-1/2 rounded-full bg-[#22d3ee] shadow-[0_0_14px_#22d3ee]' />
                    <span className='absolute left-0 top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#34d399] shadow-[0_0_16px_#34d399]' />
                </div>
                <div className='absolute inset-[34%] rounded-full bg-[radial-gradient(circle,oklch(0.74_0.16_292/0.28),transparent_70%)] blur-3xl' />
            </div>

            <div className='mx-auto max-w-4xl px-5 text-center sm:px-8'>
                <p className='animate-in fade-in slide-in-from-bottom-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur duration-700'>
                    <Sparkles className='size-4 text-primary' aria-hidden='true' />
                    21 AI agents. 1 conversation.
                </p>

                <h1 className='animate-in fade-in slide-in-from-bottom-4 mt-7 font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-balance duration-700 sm:text-6xl lg:text-7xl'>
                    Talk to one agent.{' '}
                    <span className='bg-gradient-to-r from-[#c4b5fd] via-[#e879f9] to-[#22d3ee] bg-clip-text text-transparent'>
                        Twenty get to work.
                    </span>
                </h1>

                <p className='animate-in fade-in slide-in-from-bottom-4 mx-auto mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground text-pretty delay-150 duration-700 fill-mode-backwards sm:text-lg'>
                    {siteConfig.name} is a team of {agentCount} AI agents that promotes your product, finds leads, writes your emails and fixes your bugs. You only talk to the Coordinator. It plans the work, hands it to the right specialists and brings back the results.
                </p>

                <div className='animate-in fade-in slide-in-from-bottom-4 mt-10 flex flex-col items-center justify-center gap-3 delay-300 duration-700 fill-mode-backwards sm:flex-row'>
                    <a
                        href='#demo'
                        className='inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-[0_0_40px_oklch(0.74_0.16_292/0.45)] transition-opacity hover:opacity-90 sm:w-auto'
                    >
                        Book a demo
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </a>
                    <a
                        href='#how-it-works'
                        className='inline-flex w-full items-center justify-center rounded-full border border-white/15 bg-white/[0.03] px-6 py-3.5 text-sm font-semibold transition-colors hover:bg-white/[0.08] sm:w-auto'
                    >
                        See how it works
                    </a>
                </div>

                <ul className='mt-10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2'>
                    {teams.map((team) => (
                        <li key={team.id} className='inline-flex items-center gap-2 text-sm text-muted-foreground'>
                            <span className='size-2 rounded-full' style={{ backgroundColor: team.color, boxShadow: `0 0 10px ${team.color}` }} aria-hidden='true' />
                            {team.name}
                        </li>
                    ))}
                </ul>
            </div>

            <div className='mx-auto mt-16 max-w-4xl px-5 sm:mt-20 sm:px-8'>
                <dl className='grid grid-cols-2 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur sm:grid-cols-4'>
                    {stats.map((stat, index) => (
                        <div
                            key={stat.label}
                            className={cn(
                                'flex flex-col items-center gap-1 border-white/10 px-4 py-6',
                                index % 2 === 1 && 'border-l',
                                index > 1 && 'border-t sm:border-t-0',
                                index === 2 && 'sm:border-l'
                            )}
                        >
                            <dt className='order-2 text-xs text-muted-foreground sm:text-sm'>
                                {stat.label}
                            </dt>
                            <dd className='order-1 font-heading text-3xl font-semibold tracking-tight sm:text-4xl'>
                                {stat.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </section>
    )
}

export default HeroSection
